import { Header, Sidebar } from '@/components/layout';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { hasImpersonationAccess, useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Activity, Calendar, FileText, Loader2, Pencil, Plus, Search, Trash2, UserCog, Users } from 'lucide-react';
import { useEffect, useState } from 'react';

interface User {
  id: number;
  auth_account_id?: string | null;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  ssn: string;
  roles: string[]; // transcriber_db authority data (foreign display, not a scribe authz signal)
  // Clinical-discipline labels. transcriber_db's admin_get_all_users -> User.to_json does
  // NOT yet emit this field, so it is optional and renders empty until transcriber_db adds
  // `labels`/`disciplines` to to_json (cross-repo coordination item).
  labels?: string[];
  is_active: boolean;
  clinic_id: number | null; // For backward compatibility
  clinic_name: string | null; // For backward compatibility
  clinics: { id: number; name: string }[]; // Array of assigned clinics
  allow_admin_impersonation?: boolean;
  // Auth-sourced admin status (BFF-enriched from auth's rolesBatchGet). Controls
  // ONLY whether the impersonate button is hidden here — not an authz signal.
  is_admin?: boolean;
}

interface BasePrompt {
  id: number;
  name: string;
  description: string | null;
  system_prompt: string;
  is_default_base: boolean;
  created_at: string | null;
  updated_at: string | null;
}

interface AdminStats {
  active_users: number;
  total_sessions: number;
  sessions_today: number;
  total_users: number;
}

export default function Admin() {
  const [users, setUsers] = useState<User[]>([]);
  const [basePrompts, setBasePrompts] = useState<BasePrompt[]>([]);
  const [adminStats, setAdminStats] = useState<AdminStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPromptsLoading, setIsPromptsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<BasePrompt | null>(null);
  const [promptName, setPromptName] = useState('');
  const [promptDescription, setPromptDescription] = useState('');
  const [promptSystemText, setPromptSystemText] = useState('');
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<BasePrompt | null>(null);
  const { toast } = useToast();
  const { user: authUser, startImpersonation } = useAuth();

  useEffect(() => {
    fetchUsers();
    fetchBasePrompts();
    fetchAdminStats();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/api/users', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        toast({
          title: 'Villa',
          description: 'Mistókst að sækja notendur.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp við að sækja notendur.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBasePrompts = async () => {
    try {
      const response = await fetch('/api/admin/prompts', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setBasePrompts(data || []);
      } else {
        toast({
          title: 'Villa',
          description: 'Mistókst að sækja grunnsnið.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching base prompts:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp við að sækja grunnsnið.',
        variant: 'destructive',
      });
    } finally {
      setIsPromptsLoading(false);
    }
  };

  const fetchAdminStats = async () => {
    try {
      const response = await fetch('/api/admin/dashboard/stats', {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
    } finally {
      setIsStatsLoading(false);
    }
  };

  const handleImpersonate = async (targetUser: User) => {
    const userName =
      targetUser.first_name && targetUser.last_name
        ? `${targetUser.first_name} ${targetUser.last_name}`
        : targetUser.username || targetUser.email;

    if (!targetUser.auth_account_id) {
      toast({
        title: 'Villa',
        description: 'Notandi er ekki tengdur auðkenningu og því ekki hægt að innskrá sem hann.',
        variant: 'destructive',
      });
      return;
    }

    const success = await startImpersonation(targetUser.auth_account_id);

    if (success) {
      toast({
        title: 'Innskráning sem notanda',
        description: `Þú ert nú innskráð/ur sem ${userName}. Farðu á forsíðu til að nota kerfið.`,
      });
      // Redirect to home page after impersonation
      window.location.href = '/';
    } else {
      toast({
        title: 'Villa',
        description: 'Mistókst að innskrá sem notanda. Notandi kann að hafa slökkt á þessari aðgerð.',
        variant: 'destructive',
      });
    }
  };

  const openPromptDialog = (prompt?: BasePrompt) => {
    if (prompt) {
      setEditingPrompt(prompt);
      setPromptName(prompt.name);
      setPromptDescription(prompt.description || '');
      setPromptSystemText(prompt.system_prompt || '');
    } else {
      setEditingPrompt(null);
      setPromptName('');
      setPromptDescription('');
      setPromptSystemText('');
    }
    setPromptDialogOpen(true);
  };

  const handleSavePrompt = async () => {
    if (!promptName.trim()) {
      toast({
        title: 'Villa',
        description: 'Nafn grunnsniðs má ekki vera tómt.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingPrompt(true);
    try {
      const isEditing = !!editingPrompt;
      const url = isEditing ? `/api/admin/prompts/${editingPrompt.id}` : '/api/admin/prompts';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          name: promptName.trim(),
          description: promptDescription.trim() || null,
          system_prompt: promptSystemText,
        }),
      });

      if (response.ok) {
        toast({
          title: isEditing ? 'Grunnsnið uppfært' : 'Grunnsnið búið til',
          description: isEditing
            ? `Grunnsniðið ${promptName} hefur verið uppfært.`
            : `Grunnsniðið ${promptName} hefur verið búið til.`,
        });
        setPromptDialogOpen(false);
        setEditingPrompt(null);
        setPromptName('');
        setPromptDescription('');
        setPromptSystemText('');
        fetchBasePrompts();
      } else {
        const data = await response.json();
        toast({
          title: 'Villa',
          description: data.error || data.detail || 'Mistókst að vista grunnsnið.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error saving prompt:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp við að vista grunnsnið.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingPrompt(false);
    }
  };

  const deletePrompt = async () => {
    if (!promptToDelete) return;

    try {
      const response = await fetch(`/api/admin/prompts/${promptToDelete.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast({
          title: 'Grunnsnið eytt',
          description: `Grunnsniðið ${promptToDelete.name} hefur verið eytt.`,
        });
        setPromptToDelete(null);
        fetchBasePrompts();
      } else {
        const data = await response.json();
        toast({
          title: 'Villa',
          description: data.error || 'Mistókst að eyða grunnsnið.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
      toast({
        title: 'Villa',
        description: 'Óvænt villa kom upp við að eyða grunnsnið.',
        variant: 'destructive',
      });
    }
  };

  // Filter users based on search query
  const filteredUsers = users.filter((user) => {
    if (!searchQuery) return true;

    const query = searchQuery.toLowerCase();
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase();
    const username = (user.username || '').toLowerCase();
    const email = (user.email || '').toLowerCase();
    const ssn = (user.ssn || '').toLowerCase();

    return fullName.includes(query) || username.includes(query) || email.includes(query) || ssn.includes(query);
  });

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Kerfisumsjón" />
        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-6xl space-y-8">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="font-medium text-muted-foreground text-sm">Virkir notendur</CardTitle>
                  <Users className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-2xl">
                      {isStatsLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      ) : (
                        (adminStats?.active_users ?? 0)
                      )}
                    </span>
                    {adminStats && (
                      <span className="text-muted-foreground text-xs">af {adminStats.total_users} samtals</span>
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="font-medium text-muted-foreground text-sm">Heildarfjöldi lota</CardTitle>
                  <Activity className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-2xl">
                    {isStatsLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      (adminStats?.total_sessions ?? 0).toLocaleString('is-IS')
                    )}
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="font-medium text-muted-foreground text-sm">Lotur í dag</CardTitle>
                  <Calendar className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="font-bold text-2xl">
                    {isStatsLoading ? (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    ) : (
                      (adminStats?.sessions_today ?? 0)
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Notendayfirlit</CardTitle>
                <div className="relative w-64">
                  <Search className="absolute top-2.5 left-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Leita að notanda..."
                    className="h-9 pl-9"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nafn</TableHead>
                        <TableHead>Kennitala</TableHead>
                        <TableHead>Hlutverk og fagsvið</TableHead>
                        <TableHead>Staða</TableHead>
                        <TableHead className="text-right">Aðgerðir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                            {searchQuery ? 'Engir notendur fundust' : 'Engir notendur í kerfinu'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredUsers.map((user) => (
                          <TableRow key={user.id}>
                            <TableCell className="font-medium">
                              {user.first_name && user.last_name
                                ? `${user.first_name} ${user.last_name}`
                                : user.username || user.email}
                            </TableCell>
                            <TableCell className="font-mono">{user.ssn || 'N/A'}</TableCell>
                            <TableCell>
                              <div className="flex max-w-[250px] flex-col gap-1.5">
                                {/* Roles: transcriber_db authority data (foreign display). */}
                                <div className="flex flex-wrap gap-1">
                                  {user.roles.length > 0 ? (
                                    user.roles.map((role) => (
                                      <Badge key={role} variant="secondary" className="px-1 font-mono text-[10px]">
                                        {role}
                                      </Badge>
                                    ))
                                  ) : (
                                    <span className="text-muted-foreground text-xs italic">Engin hlutverk</span>
                                  )}
                                </div>
                                {/* Clinical-discipline labels (empty until transcriber_db to_json emits them). */}
                                {user.labels && user.labels.length > 0 && (
                                  <div className="flex flex-wrap gap-1">
                                    {user.labels.map((label) => (
                                      <Badge key={label} variant="outline" className="px-1 font-mono text-[10px]">
                                        {label}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium text-xs ${
                                  user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {user.is_active ? 'Virkur' : 'Óvirkur'}
                              </span>
                            </TableCell>
                            <TableCell className="space-x-1 text-right">
                              {/* Impersonate button — visibility requires the viewer's
                                  accounts:impersonate permission (no role read) AND hides for
                                  admin targets via the auth-sourced is_admin (BFF-enriched from
                                  auth's rolesBatchGet, not a transcriber_db role string). The
                                  button is also disabled when the target has opted out of admin
                                  impersonation. The BFF remains the true gate: POST
                                  /api/admin/impersonate independently fails closed on the auth
                                  is_admin status (403 for admin targets) and on the
                                  allow_admin_impersonation consent flag, so this UX hide is a
                                  courtesy, not the authz boundary. */}
                              {hasImpersonationAccess(authUser) && !user.is_admin && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleImpersonate(user)}
                                  disabled={user.allow_admin_impersonation === false}
                                  className="text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                                  title={
                                    user.allow_admin_impersonation === false
                                      ? 'Notandi hefur slökkt á þessari aðgerð'
                                      : 'Innskrá sem notanda'
                                  }
                                >
                                  <UserCog className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Base Prompts Management */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Grunnsnið (Base Prompts)
                </CardTitle>
                <Button onClick={() => openPromptDialog()} size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  Nýtt grunnsnið
                </Button>
              </CardHeader>
              <CardContent>
                {isPromptsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : basePrompts.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">Engin grunnsnið í kerfinu</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nafn</TableHead>
                        <TableHead>Lýsing</TableHead>
                        <TableHead>Sjálfgefið</TableHead>
                        <TableHead>Uppfært</TableHead>
                        <TableHead className="text-right">Aðgerðir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {basePrompts.map((prompt) => (
                        <TableRow key={prompt.id}>
                          <TableCell className="font-medium">{prompt.name}</TableCell>
                          <TableCell className="max-w-[200px] truncate text-muted-foreground text-sm">
                            {prompt.description || '-'}
                          </TableCell>
                          <TableCell>
                            {prompt.is_default_base && <Badge variant="secondary">Sjálfgefið</Badge>}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-sm">
                            {prompt.updated_at ? new Date(prompt.updated_at).toLocaleDateString('is-IS') : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openPromptDialog(prompt)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPromptToDelete(prompt)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Delete Prompt Confirmation Dialog */}
      <AlertDialog open={!!promptToDelete} onOpenChange={(open) => !open && setPromptToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ertu viss um að þú viljir eyða þessu grunnsniði?</AlertDialogTitle>
            <AlertDialogDescription>
              Þessi aðgerð er óafturkræf. Grunnsniðið <strong>{promptToDelete?.name}</strong> verður varanlega fjarlægt
              úr kerfinu.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hætta við</AlertDialogCancel>
            <AlertDialogAction
              onClick={deletePrompt}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eyða grunnsniði
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Prompt Dialog */}
      <Dialog open={promptDialogOpen} onOpenChange={setPromptDialogOpen}>
        <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
          <DialogHeader>
            <DialogTitle>{editingPrompt ? `Breyta grunnsniði: ${editingPrompt.name}` : 'Nýtt grunnsnið'}</DialogTitle>
            <DialogDescription>
              {editingPrompt
                ? 'Breyttu nafni, lýsingu eða kerfisboði fyrir þetta grunnsnið.'
                : 'Búðu til nýtt grunnsnið með nafni, lýsingu og kerfisboði.'}
            </DialogDescription>
          </DialogHeader>
          <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-4">
            <div className="grid gap-2">
              <Label htmlFor="prompt-name">Nafn</Label>
              <Input
                id="prompt-name"
                placeholder="t.d. Almenn heimsókn"
                value={promptName}
                onChange={(e) => setPromptName(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="prompt-description">Lýsing (valfrjálst)</Label>
              <Input
                id="prompt-description"
                placeholder="Stutt lýsing á grunnsniðinu..."
                value={promptDescription}
                onChange={(e) => setPromptDescription(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="prompt-system">Kerfisboð (System Prompt)</Label>
              <Textarea
                id="prompt-system"
                placeholder="Sláðu inn kerfisboðið..."
                value={promptSystemText}
                onChange={(e) => setPromptSystemText(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromptDialogOpen(false)}>
              Hætta við
            </Button>
            <Button onClick={handleSavePrompt} disabled={isSavingPrompt}>
              {isSavingPrompt && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingPrompt ? 'Vista breytingar' : 'Búa til grunnsnið'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
